import { AddTableLight } from '@repo/go-icons';
import { Button, Collapse, ConfigContext, Drawer } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useContext, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import type { AlertRulesTableStore } from '../AlertRulesTableStore';
import type { AlertRulesService } from '../services/AlertRulesService';

import { openCreateTemplateModal } from './openCreateTemplateModal';
import { TemplateItem } from './TemplateItem';

interface Props {
  isOpen: boolean;
  onClose: VoidFunction;
  externalContainer?: Element;
  service: AlertRulesService;
  store: AlertRulesTableStore;
}

type CSSPropertiesWithVariables = CSSProperties & Record<`--${string}`, string | number>;

const style: CSSPropertiesWithVariables = {
  '--go-collapse-body-padding-right': 0,
  '--go-collapse-body-padding-left': 0,
  '--go-collapse-body-padding-top': '4px',
  '--go-collapse-body-padding-bottom': '8px',
};

export const TemplatesDrawer = observer(function TemplatesDrawer({
  store,
  service,
  isOpen,
  onClose,
  externalContainer,
}: Props) {
  const { t } = useTranslation();
  const templateManager = service.alertRulesTemplatesManager;
  const configProvider = useContext(ConfigContext);
  const { i18n } = useTranslation();
  return (
    <Drawer
      title={t('alertsManagerWidget:alertRules.templatesDrawer.title')}
      onClose={onClose}
      open={isOpen}
      container={externalContainer}
    >
      <div style={{ width: 260 }}>
        <Collapse defaultValue={['Public', 'Personal']}>
          <Collapse.Panel
            header={t('alertsManagerWidget:alertRules.templatesDrawer.public')}
            panelKey="Public"
            key="Public"
            styleWrapper={style}
            stickyHeader
          >
            {templateManager.publicTemplates?.map((template) => (
              <TemplateItem key={template.id} template={template} templateManager={templateManager} service={service} />
            ))}
          </Collapse.Panel>
          <Collapse.Panel
            header={t('alertsManagerWidget:alertRules.templatesDrawer.personal')}
            panelKey="Personal"
            key="Personal"
            styleWrapper={style}
            stickyHeader
          >
            {templateManager.personalTemplates?.map((template) => (
              <TemplateItem key={template.id} template={template} templateManager={templateManager} service={service} />
            ))}
          </Collapse.Panel>
        </Collapse>
      </div>
      <div style={{ padding: '0 8px 16px 16px', marginTop: 'auto' }}>
        <Button
          variant="success"
          icon={<AddTableLight />}
          style={{ width: '100%' }}
          disabled={store.selectedRows.length === 0 || !store.isEditing}
          onClick={() => {
            openCreateTemplateModal(
              service,
              store.selectedRows.map((id) => service.alertRulesMap.get(id as string)).filter((rule) => rule != null),
              configProvider,
              i18n,
            );
          }}
        >
          {t('alertsManagerWidget:alertRules.templatesDrawer.createTemplate')}
        </Button>
      </div>
    </Drawer>
  );
});
